import DottedMap from "dotted-map";
import {
  Activity,
  CircleDollarSign,
  Gauge,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const map = new DottedMap({ height: 55, grid: "diagonal" });
const mapPoints = map.getPoints();

const chartData = [
  { month: "May", performance: 56, baseline: 62 },
  { month: "Jun", performance: 68, baseline: 64 },
  { month: "Jul", performance: 72, baseline: 66 },
  { month: "Aug", performance: 81, baseline: 68 },
  { month: "Sep", performance: 86, baseline: 70 },
  { month: "Oct", performance: 94, baseline: 72 },
];

const signals = [
  { title: "Equipment alert", detail: "Excavator A-301 · service threshold", time: "1m", icon: Wrench },
  { title: "Contract event", detail: "Uptime SLA verified automatically", time: "3m", icon: ShieldCheck },
  { title: "Performance report", detail: "Line efficiency improved by 12%", time: "6m", icon: Gauge },
];

const locations = [
  { label: "US · Automated warehouses", className: "left-[2%] top-[47%]" },
  { label: "DE · Process plant", className: "left-[43%] top-[28%]" },
  { label: "ES · Robotic CNC", className: "left-[37%] top-[52%]" },
  { label: "SE · Production line", className: "left-[52%] top-[8%]" },
];

const StatusLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-3 flex items-center gap-2 text-sm font-medium text-muted-foreground">
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10">
      <span className="h-2 w-2 rounded-full bg-primary" />
    </span>
    {children}
  </div>
);

const WorldMap = () => (
  <svg viewBox="0 0 120 60" className="h-auto w-full text-primary/55" aria-label="Global industrial assets map" role="img">
    {mapPoints.map((point, index) => (
      <circle key={`${point.x}-${point.y}-${index}`} cx={point.x} cy={point.y} r="0.15" fill="currentColor" />
    ))}
  </svg>
);

const PerformanceShowcase = () => (
  <section className="mb-12" aria-label="Connected performance capabilities">
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <article className="glass-card flex min-h-[360px] flex-col justify-between overflow-hidden rounded-lg p-6 shadow-md transition-shadow hover:shadow-lg">
        <div>
          <StatusLabel>Industrial asset monitoring</StatusLabel>
          <h3 className="max-w-xl text-xl font-semibold leading-snug text-foreground">
            Track equipment across global operations. <span className="font-normal text-muted-foreground">Monitor, compare, and optimise performance in real time.</span>
          </h3>
        </div>
        <div className="relative mt-8 min-h-[210px] overflow-hidden">
          <WorldMap />
          {locations.map((location) => (
            <div key={location.label} className={`absolute ${location.className} max-w-[148px] rounded-md border border-border bg-card/95 px-2 py-1 text-[10px] font-medium leading-tight text-card-foreground shadow-md`}>
              {location.label}
            </div>
          ))}
        </div>
      </article>

      <article className="glass-card flex min-h-[360px] flex-col rounded-lg p-6 shadow-md transition-shadow hover:shadow-lg">
        <div>
          <StatusLabel>Connected enterprise</StatusLabel>
          <h3 className="max-w-xl text-xl font-semibold leading-snug text-foreground">
            Machine events become decision signals. <span className="font-normal text-muted-foreground">Telemetry triggers governance, service, and commercial actions.</span>
          </h3>
        </div>
        <div className="mt-8 space-y-2.5">
          {signals.map(({ title, detail, time, icon: Icon }) => (
            <div key={title} className="flex items-center gap-3 rounded-md border border-border bg-card p-3.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-foreground">{title}</p>
                  <span className="text-xs text-muted-foreground">{time}</span>
                </div>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">{detail}</p>
              </div>
            </div>
          ))}
        </div>
      </article>

      <article className="glass-card flex min-h-[340px] flex-col rounded-lg p-6 shadow-md transition-shadow hover:shadow-lg">
        <div>
          <StatusLabel>Asset performance</StatusLabel>
          <h3 className="text-xl font-semibold leading-snug text-foreground">
            Measure outcomes against the baseline. <span className="font-normal text-muted-foreground">See whether every asset is delivering its contracted value.</span>
          </h3>
        </div>
        <div className="mt-5 h-56 w-full" aria-label="Asset performance trend from May to October">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 8, left: -22, bottom: 0 }}>
              <defs>
                <linearGradient id="performanceFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                  <stop offset="90%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
              <YAxis domain={[40, 100]} axisLine={false} tickLine={false} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
              <Tooltip
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, color: "hsl(var(--card-foreground))" }}
                labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 600 }}
              />
              <Area type="monotone" dataKey="baseline" name="Contract baseline" stroke="hsl(var(--muted-foreground))" strokeWidth={1.5} fill="transparent" strokeDasharray="4 4" />
              <Area type="monotone" dataKey="performance" name="Verified performance" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#performanceFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </article>

      <div className="grid min-h-[340px] grid-cols-1 gap-4 sm:grid-cols-2">
        <article className="glass-card flex flex-col justify-between rounded-lg p-5 shadow-md transition-shadow hover:shadow-lg">
          <div>
            <StatusLabel>Continuous assurance</StatusLabel>
            <h3 className="text-lg font-semibold text-foreground">Auditable by design</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Every KPI, service event, and SLA decision stays traceable from source signal to outcome.</p>
          </div>
          <div className="mt-6 rounded-md border border-border bg-muted/40 p-4">
            <ShieldCheck className="h-6 w-6 text-primary" />
            <div className="mt-4 flex items-end justify-between gap-3">
              <div><p className="text-2xl font-semibold text-foreground">98.7%</p><p className="text-xs text-muted-foreground">Evidence coverage</p></div>
              <span className="text-xs font-medium text-primary">Verified</span>
            </div>
          </div>
        </article>

        <article className="glass-card flex flex-col justify-between rounded-lg p-5 shadow-md transition-shadow hover:shadow-lg">
          <div>
            <StatusLabel>Commercial alignment</StatusLabel>
            <h3 className="text-lg font-semibold text-foreground">Outcomes drive value</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Translate verified machine performance into transparent payment and incentive events.</p>
          </div>
          <div className="mt-6 rounded-md border border-border bg-muted/40 p-4">
            <CircleDollarSign className="h-6 w-6 text-primary" />
            <div className="mt-4 flex items-end justify-between gap-3">
              <div><p className="text-2xl font-semibold text-foreground">+12%</p><p className="text-xs text-muted-foreground">Outcome uplift</p></div>
              <Activity className="h-5 w-5 text-primary" />
            </div>
          </div>
        </article>
      </div>
    </div>
  </section>
);

export default PerformanceShowcase;